/**
 * Captura de valores por período. Lista las metric_definitions visibles
 * al usuario, agrupadas por nivel; cada una con su último valor del
 * período seleccionado y un input para capturar/actualizar.
 *
 * Nivel `project` no se captura aquí — esos viven en la card del proyecto
 * (apps/web/src/features/projects/ProjectMetricsCard.tsx).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  computeStatus,
  type KpiDefinition,
  type KpiValue,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { EvaluationsTabs } from "@/features/evaluations/EvaluationsTabs";
import { api } from "@/lib/api";
import { canCaptureDef, canSeeDef } from "@/lib/eval-visibility";

export const Route = createFileRoute("/evaluations/kpis")({
  component: MetricsCapturePage,
});

function currentPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function MetricsCapturePage() {
  const [period, setPeriod] = useState(currentPeriod());
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  const defsQ = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["metric-definitions"],
    queryFn: () => api.get("/kpi-definitions"),
  });

  // Valores del período actual (sin filtro de definitionId para traer todos los visibles).
  const valuesQ = useQuery<{
    values: Array<{ value: KpiValue; definition: KpiDefinition }>;
  }>({
    queryKey: ["metric-values", "period", period],
    queryFn: () => api.get(`/kpi-values?periodFrom=${period}&periodTo=${period}&projectId=`),
  });

  const valueByDef = useMemo(() => {
    const m = new Map<string, KpiValue>();
    for (const v of valuesQ.data?.values ?? []) m.set(v.value.definitionId, v.value);
    return m;
  }, [valuesQ.data]);

  // Filtro por visibility + excluir nivel proyecto (esos se capturan en la card del proyecto).
  const visibleDefs = useMemo(() => {
    if (!me.data || !defsQ.data) return [];
    return defsQ.data.definitions
      .filter((d) => d.level !== "project")
      .filter((d) => canSeeDef(d.visibility, me.data.role));
  }, [me.data, defsQ.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="evaluaciones"
        title="Captura de métricas"
        subtitle="Captura valores numéricos por período. Los valores con auto-source pueden recargarse automáticamente."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/evaluations" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            {me.data && (me.data.role === "admin" || me.data.role === "finanzas") && (
              <RunAutoButton period={period} />
            )}
          </div>
        }
      />

      <EvaluationsTabs current="kpis" role={me.data?.role ?? "member"} />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-xs uppercase tracking-wider text-base-content/55">Período</span>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value || currentPeriod())}
            className="input input-bordered input-sm"
          />
        </label>
      </div>

      {(defsQ.isLoading || valuesQ.isLoading) && (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md text-primary" />
        </div>
      )}

      {!defsQ.isLoading &&
        EVAL_LEVELS.filter((l) => l !== "project").map((level) => {
          const defs = visibleDefs.filter((d) => d.level === level);
          if (defs.length === 0) return null;
          return (
            <section key={level} className="space-y-3">
              <h2 className="font-display text-xl font-semibold">{EVAL_LEVEL_LABELS[level]}</h2>
              <ul className="space-y-2">
                {defs.map((d) => (
                  <MetricRow
                    key={d.id}
                    def={d}
                    value={valueByDef.get(d.id)}
                    period={period}
                    canCapture={!!me.data && canCaptureDef(d.visibility, me.data.role)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
    </div>
  );
}

function MetricRow({
  def,
  value,
  period,
  canCapture,
}: {
  def: KpiDefinition;
  value: KpiValue | undefined;
  period: string;
  canCapture: boolean;
}) {
  const qc = useQueryClient();
  const [input, setInput] = useState<string>(value?.value != null ? String(value.value) : "");
  const [note, setNote] = useState<string>(value?.note ?? "");
  useEffect(() => {
    setInput(value?.value != null ? String(value.value) : "");
    setNote(value?.note ?? "");
  }, [value?.value, value?.note]);

  const save = useMutation({
    mutationFn: () => {
      const parsed = input === "" ? undefined : Number(input);
      if (parsed !== undefined && Number.isNaN(parsed)) throw new Error("valor inválido");
      return api.post("/kpi-values", {
        definitionId: def.id,
        period,
        value: parsed,
        note: note || undefined,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metric-values"] }),
  });

  const status = computeStatus(value?.value ?? null, def);
  const statusClass =
    status === "good"
      ? "border-l-success"
      : status === "improvable"
        ? "border-l-warning"
        : status === "bad"
          ? "border-l-error"
          : "border-l-base-300";

  const isDirty =
    input !== (value?.value != null ? String(value.value) : "") || note !== (value?.note ?? "");

  return (
    <li className={`card-elevated p-4 border-l-4 ${statusClass}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium">{def.name}</span>
            {value?.autoFilled && (
              <span className="badge badge-success badge-xs gap-1">
                <Sparkles className="size-3" /> auto
              </span>
            )}
          </div>
          {def.description && (
            <p className="text-xs text-base-content/55 mb-1.5">{def.description}</p>
          )}
          <div className="text-xs text-base-content/55 flex items-center gap-2 flex-wrap">
            {def.unit && <span className="font-mono">{def.unit}</span>}
            <span className="text-success">verde {def.thresholdGood ?? "—"}</span>
            <span>·</span>
            <span className="text-warning">amarillo {def.thresholdImprovable ?? "—"}</span>
            <span>·</span>
            <span className="text-error">rojo {def.thresholdBad ?? "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="any"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!canCapture}
            className="input input-bordered input-sm w-28 text-right tabular-nums"
            placeholder="—"
          />
          <button
            type="button"
            disabled={!canCapture || !isDirty || save.isPending}
            onClick={() => save.mutate()}
            className="btn btn-primary btn-sm"
          >
            {save.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Check className="size-4" />
            )}
            Guardar
          </button>
        </div>
      </div>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={!canCapture}
        className="input input-bordered input-xs w-full mt-2"
        placeholder="Nota (opcional)"
      />
    </li>
  );
}

function RunAutoButton({ period }: { period: string }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post(`/kpi-values/run-auto?period=${period}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metric-values"] }),
  });
  return (
    <button
      type="button"
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
      className="btn btn-secondary btn-sm gap-1.5"
      title="Calcular métricas automáticas para este período"
    >
      {mut.isPending ? (
        <span className="loading loading-spinner loading-xs" />
      ) : (
        <Sparkles className="size-4" />
      )}
      Calcular auto
    </button>
  );
}
